import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useProfessionalRequestCopy } from "@/hooks/useProfessionalRequestCopy";
import { cn } from "@/lib/utils";
import { formatUyMinor } from "@/lib/formatUyMinor";
import type { ProfessionalPricingDto } from "@/services/professionalEarningsService";
import type { NotarialRequestStatus } from "@/services/notaryRequestsService";

function formatUrUnitsDisplay(units: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(units);
}

function PricingLine({
  label,
  description,
  amount,
}: {
  label: string;
  description?: string | null;
  amount: string;
}): JSX.Element {
  return (
    <div className="flex justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <p className="shrink-0 text-sm tabular-nums text-foreground">{amount}</p>
    </div>
  );
}

function hasServiceFeeBreakdown(pricing: ProfessionalPricingDto): boolean {
  return (
    typeof pricing.serviceFixedMinor === "number" &&
    typeof pricing.serviceUrVariableMinor === "number" &&
    typeof pricing.fixedAmountIncludesIva === "boolean" &&
    typeof pricing.urVariableIncludesIva === "boolean"
  );
}

export function ProfessionalRequestPricingCard({
  pricing,
  requestType,
  requestStatus,
}: {
  pricing: ProfessionalPricingDto;
  requestType: string;
  requestStatus: NotarialRequestStatus;
}): JSX.Element {
  const { tp, tpo, tpt } = useProfessionalRequestCopy();

  const [open, setOpen] = useState(() => requestStatus === "pending");

  useEffect(() => {
    if (requestStatus !== "pending") {
      setOpen(false);
    }
  }, [requestStatus]);

  const typeKey = requestType.trim() || "notarial_certificate";
  const certificateKindLabel = tpt("requestDetail.pricingLineService", typeKey);

  const showBreakdown = hasServiceFeeBreakdown(pricing);
  const fixedMinor = pricing.serviceFixedMinor ?? 0;
  const urVarMinor = pricing.serviceUrVariableMinor ?? 0;
  const ivaOnFixed = pricing.ivaOnFixedMinor ?? 0;
  const ivaOnUr = pricing.ivaOnUrMinor ?? 0;
  const deliveryDeduction = pricing.digitalDeliveryDeductionMinor ?? 0;
  const urUnits = pricing.urUnits ?? null;
  const urIndexMinor = pricing.urIndexMinorPerUnit ?? null;
  const urIva = pricing.urVariableIncludesIva ?? true;

  let urNetLineLabel = "";
  if (urVarMinor > 0) {
    if (urUnits != null && urUnits > 0) {
      urNetLineLabel = tpo("requestDetail.pricingUrLineGross", {
        units: formatUrUnitsDisplay(urUnits),
      });
    } else {
      urNetLineLabel = tp("requestDetail.pricingUrVariableFallback");
    }
  }

  const urNetDescriptionParts: string[] = [];
  if (ivaOnUr <= 0 && urIva && urVarMinor > 0) {
    urNetDescriptionParts.push(tp("requestDetail.pricingVatIncludedShort"));
  }
  if (urIndexMinor != null && urIndexMinor > 0 && urUnits != null && urUnits > 0) {
    urNetDescriptionParts.push(
      tpo("requestDetail.pricingUrOneUnitValue", {
        amount: formatUyMinor(urIndexMinor, pricing.currency),
      }),
    );
  }
  const urNetDescription = urNetDescriptionParts.length > 0 ? urNetDescriptionParts.join(" · ") : null;

  const showServiceFallbackRow =
    showBreakdown && fixedMinor <= 0 && urVarMinor <= 0 && pricing.serviceBaseMinor > 0;

  const totalFormatted = formatUyMinor(pricing.totalProfessionalMinor, pricing.currency);

  const breakdownSection = (
    <div className="rounded-lg border border-border bg-card">
      {showBreakdown ? (
        <p className="border-b border-border px-4 pb-2 pt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {tp("requestDetail.pricingServiceFeeSection")}
        </p>
      ) : null}
      <div className={`divide-y divide-border px-4 ${showBreakdown ? "" : "pt-1"}`}>
        {showBreakdown ? (
          <>
            {fixedMinor > 0 ? (
              <>
                <PricingLine
                  label={tp("requestDetail.pricingLineFixedFee")}
                  amount={formatUyMinor(fixedMinor, pricing.currency)}
                />
                {ivaOnFixed > 0 ? (
                  <PricingLine
                    label={tp("requestDetail.pricingLineIvaOnFixed")}
                    amount={formatUyMinor(ivaOnFixed, pricing.currency)}
                  />
                ) : null}
              </>
            ) : null}
            {urVarMinor > 0 ? (
              <>
                <PricingLine
                  label={urNetLineLabel}
                  description={urNetDescription}
                  amount={formatUyMinor(urVarMinor, pricing.currency)}
                />
                {ivaOnUr > 0 ? (
                  <PricingLine
                    label={tp("requestDetail.pricingLineIvaOnUr")}
                    amount={formatUyMinor(ivaOnUr, pricing.currency)}
                  />
                ) : null}
              </>
            ) : null}
            {showServiceFallbackRow ? (
              <PricingLine
                label={certificateKindLabel}
                amount={formatUyMinor(pricing.serviceBaseMinor, pricing.currency)}
              />
            ) : null}
          </>
        ) : (
          <PricingLine
            label={certificateKindLabel}
            amount={formatUyMinor(pricing.serviceBaseMinor, pricing.currency)}
          />
        )}
        {deliveryDeduction > 0 ? (
          <PricingLine
            label={tp("requestDetail.pricingDigitalDeliveryDeduction")}
            description={tp("requestDetail.pricingDigitalDeliveryDeductionSubtitle")}
            amount={`- ${formatUyMinor(deliveryDeduction, pricing.currency)}`}
          />
        ) : null}
        {(pricing.professionalStampMinor ?? 0) > 0 ? (
          <PricingLine
            label={tp("requestDetail.pricingProfessionalStamp")}
            amount={formatUyMinor(pricing.professionalStampMinor!, pricing.currency)}
          />
        ) : null}
      </div>
      <div className="mt-1 rounded-b-lg border-t border-border bg-muted/50 px-4 py-4">
        <div className="flex justify-between gap-4">
          <p className="text-base font-semibold text-foreground">
            {tp("requestDetail.pricingTotal")}
          </p>
          <p className="text-base font-semibold tabular-nums text-foreground">{totalFormatted}</p>
        </div>
      </div>
    </div>
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm transition-colors dark:bg-card">
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Summary row lives outside the inner card — breakdown stays on elevated Card */}
          <div className="grid grid-cols-1 items-start gap-x-4 gap-y-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-x-6">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className={cn(
                  "group flex w-full min-w-0 items-start gap-2.5 rounded-lg px-2 py-2 text-left transition-colors duration-150",
                  "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card motion-reduce:transition-none dark:hover:bg-muted/40",
                )}
                aria-expanded={open}
                aria-label={open ? tp("requestDetail.pricingToggleCollapse") : tp("requestDetail.pricingToggleExpand")}
              >
                <ChevronDown
                  className={cn(
                    "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
                    open && "rotate-180",
                  )}
                  aria-hidden
                />
                <div className="min-w-0">
                  <CardTitle className="text-base font-semibold leading-snug text-foreground">
                    {tp("requestDetail.pricingCardTitle")}
                  </CardTitle>
                  {open ? (
                    <CardDescription className="mt-1 text-sm text-muted-foreground">
                      {certificateKindLabel}
                    </CardDescription>
                  ) : (
                    <CardDescription className="mt-1 line-clamp-2 text-sm leading-snug text-muted-foreground">
                      {certificateKindLabel}
                    </CardDescription>
                  )}
                </div>
              </button>
            </CollapsibleTrigger>
            <div
              className={cn(
                "flex flex-col items-end justify-center text-right sm:max-w-[11rem]",
                "transition-opacity duration-150 motion-reduce:transition-none",
                open && "hidden",
              )}
              aria-hidden={open}
            >
              <p className="text-xs font-medium text-muted-foreground">{tp("requestDetail.pricingTotal")}</p>
              <p className="text-lg font-semibold tabular-nums leading-tight text-foreground">{totalFormatted}</p>
            </div>
          </div>
          <CollapsibleContent className="overflow-hidden">
            <Card className="transition-shadow duration-200">{breakdownSection}</Card>
          </CollapsibleContent>
        </div>
      </div>
    </Collapsible>
  );
}
